import { useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import clsx from 'clsx';

/**
 * Sidebar section with a clickable header that toggles a children
 * region. Used to keep the "开发工具 · DevTools" section from
 * dominating the left rail when the user is focused on a device.
 *
 * State: in-component `useState`. Per project rule R2 we deliberately
 * don't persist this preference — the cost of re-clicking once per
 * page reload is lower than introducing a new prefs key for it.
 */
export function CollapsibleSection({
  title,
  defaultOpen = true,
  children,
}: {
  title: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-3 first:mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          'flex w-full items-center gap-1 px-3 py-0.5 text-[10px] uppercase tracking-wider text-ink-dim',
          'rounded transition hover:text-ink',
        )}
        aria-expanded={open}
      >
        <ChevronRight
          className={clsx(
            'h-3 w-3 shrink-0 transition-transform',
            open && 'rotate-90',
          )}
        />
        <span>{title}</span>
      </button>
      {open && <div className="mt-0.5 space-y-0.5">{children}</div>}
    </div>
  );
}
